<template>
  <b-card
    id="page-charges"
    data-tour="charges-page"
    no-body
    class="mb-0 p-2"
  >
    <b-card-header
      class="pl-0 pr-0 d-flex align-items-between justify-content-center"
    >
      <b-col cols="12" md="4">
        <b-card-title
          ><h2>{{ $t(label("title")) }}</h2></b-card-title
        >
      </b-col>

      <b-col
        cols="12"
        md="8"
        class="d-flex justify-content-end align-items-center flex-wrap"
      >
        <tutorial-button
          data-tour="charges-help"
          class="mr-1 mb-50"
          :label="$t('tutorials.common.viewTutorial')"
          :disabled="loading"
          @click="startTutorial"
        />

        <div
          v-if="canCreate"
          data-tour="charges-create"
          class="mb-50"
        >
          <b-button variant="primary" @click="onCreate">
            <feather-icon
              :aria-label="$t(label('register'))"
              icon="PlusIcon"
              class="mr-sm-25"
            />
            <span class="d-none d-sm-inline text-nowrap">
              {{ $t(label("register")) }}
            </span>
          </b-button>
        </div>
      </b-col>
    </b-card-header>
    <div data-tour="charges-filters">
      <Filterss
        :fields="fildsToFilters"
        :model="model"
        @filter-applied="applyFilters"
        @filter-reset="resetFilters"
      />
    </div>
    <!-- Table for larger devices -->
    <b-table
      id="charges-table"
      data-tour="charges-table"
      responsive
      :head-variant="headVariant"
      :striped="striped"
      :bordered="bordered"
      :borderless="borderless"
      :outlined="outlined"
      :per-page="perPage"
      :busy="loading"
      :items="items"
      :fields="fields"
      class="d-none d-md-table shadow-sm !rounded"
    >
      <template #head()="data">
        <span class="text-black">{{
          $t(labelHeader(data.label.toLowerCase()))
        }}</span>
      </template>

      <template #cell(name)="data">
        <slot name="cell-name" :data="data">
          {{ data.item.name || "N/A" }}
        </slot>
      </template>

      <template #cell(actions)="data">
        <span data-tour="charges-desktop-actions">
          <feather-icon
            v-if="canShow"
            class="text-success mr-25 cursor-pointer"
            aria-label="Show details"
            size="18"
            icon="FileTextIcon"
            @click="onShow(data.item.idRol)"
          />

          <feather-icon
            v-if="canEdit"
            class="text-primary mr-25 cursor-pointer"
            aria-label="Edit details"
            size="18"
            icon="EditIcon"
            @click="onEdit(data.item.idRol)"
          />

          <feather-icon
            v-if="canDelete"
            class="text-danger cursor-pointer"
            aria-label="Delete details"
            size="18"
            icon="Trash2Icon"
            @click="onDelete(data.item.idRol)"
          />
        </span>
      </template>

      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle ml-50"></b-spinner>
          <strong>{{ $t("loading") }}...</strong>
        </div>
      </template>
    </b-table>

    <!-- pagination -->
    <div
      data-tour="charges-pagination"
      class="mx-2 d-none d-md-table"
    >
      <b-row>
        <b-col cols="12" md="2" class="mb-2">
          <b-form-group class="mb-0">
            <label class="d-inline-block text-sm-left mr-50">
              {{ $t("perPage") }}
            </label>
            <b-form-select
              id="perPageSelect"
              v-model="perPage"
              size="sm"
              :options="pageOptions"
              class="w-50"
              @change="handlePerPageChange"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="10"
          class="d-flex align-items-center justify-content-end"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            pills
            @change="handlePageChange"
            first-number
            last-number
          >
            <template #prev-text>
              <feather-icon size="18" icon="ChevronLeftIcon" />
            </template>
            <template #next-text>
              <feather-icon size="18" icon="ChevronRightIcon" />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>
    <!-- List for mobile devices -->
    <div
      id="list-container"
      data-tour="charges-mobile-list"
      class="d-block d-md-none mt-1 list-container"
    >
      <b-list-group>
        <b-list-group-item
          v-for="item in items"
          :key="item.idRol"
          class="d-flex flex-column align-items-start border border-primary rounded mb-1"
        >
          <div class="d-flex w-100 justify-content-between mb-1">
            <strong> {{ $t("name") }} : {{ item.name || "N/A" }}</strong>

            <b-dropdown
              data-tour="charges-mobile-actions"
              variant="link"
              no-caret
              class="chart-dropdown"
              toggle-class="p-0"
              right
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="18"
                  class="text-body cursor-pointer"
                />
              </template>
              <b-dropdown-item v-if="canShow" @click="onShow(item.idRol)">
                <div class="d-flex align-items-center justify-content-between">
                  {{ $t("show") }}
                  <feather-icon
                    class="text-success mr-25 cursor-pointer"
                    aria-label="Show details"
                    size="18"
                    icon="FileTextIcon"
                  />
                </div>
              </b-dropdown-item>
              <b-dropdown-item v-if="canEdit" @click="onEdit(item.idRol)">
                <div class="d-flex align-items-center justify-content-between">
                  {{ $t("edit") }}
                  <feather-icon
                    class="text-primary mr-25 cursor-pointer"
                    aria-label="Edit details"
                    size="18"
                    icon="EditIcon"
                  />
                </div>
              </b-dropdown-item>
              <b-dropdown-item v-if="canDelete" @click="onDelete(item.idRol)">
                <div class="d-flex align-items-center justify-content-between">
                  {{ $t("delete") }}
                  <feather-icon
                    class="text-danger cursor-pointer"
                    aria-label="Delete details"
                    size="18"
                    icon="Trash2Icon"
                  />
                </div>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </b-list-group-item>
        <b-list-group-item v-if="items.length == 0" class="text-center">
          {{ $t("notRegister") }}
        </b-list-group-item>
        <b-list-group-item v-if="loading" class="text-center">
          <b-spinner class="align-middle ml-50" variant="primary"></b-spinner>
        </b-list-group-item>
      </b-list-group>
    </div>

    <app-tour
      :name="tutorialName"
      :steps="tutorialSteps"
      :options="tutorialOptions"
      :callbacks="tutorialCallbacks"
      @target-not-found="onTutorialTargetNotFound"
    />
  </b-card>
</template>

<script>
import _ from "lodash";
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCol,
  BButton,
  BTable,
  BTooltip,
  BSpinner,
  BFormSelect,
  BFormFile,
  BOverlay,
  BListGroup,
  BListGroupItem,
  BDropdown,
  BDropdownItem,
  BPagination,
  BFormGroup,
  BRow,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import Filterss from "../components/filters/Filterss.vue";
import AppTour from "@core/components/app-tour/AppTour.vue";
import TutorialButton from "@core/components/app-tour/TutorialButton.vue";
import { allCharges, deleteCharge } from "@/@core/api/request/charges";
import { getParams, showLoading, stopLoading } from "@core/utils/utils";
import { canAccess } from "@/security/permissions/permissionHelper";
import { buildCustomerChargesTour } from "@/tutorials/customerCharges";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCol,
    BButton,
    BTable,
    BTooltip,
    BSpinner,
    BFormSelect,
    BFormFile,
    BOverlay,
    BListGroup,
    BListGroupItem,
    BDropdown,
    BDropdownItem,
    BPagination,
    BFormGroup,
    BRow,
    Filterss,
    AppTour,
    TutorialButton,
  },
  directives: {
    "b-tooltip": BTooltip,
    Ripple,
  },
  data: () => ({
    model: "charges",
    companyGuid: null,
    loading: false,
    totalRows: 0,
    currentPage: 1,
    perPage: 10,
    pageOptions: [10, 50, 100],
    hasNextPage: true,
    items: [],
    headVariant: "light",
    striped: false,
    outlined: false,
    bordered: true,
    borderless: false,
    fields: [
      { label: "name", key: "name", sortable: true },
      { label: "action", key: "actions", sortable: false },
    ],
    fildsToFilters: [
      {
        name: "name",
        title: "name",
        placeholder: "name",
        type: "search",
        visible: true,
      },
    ],
    appliedFilters: {},
    isMobileViewport: false,
    tutorialName: "customerChargesTour",
  }),

  computed: {
    canCreate() {
      return canAccess("Cargos", "creation");
    },
    canShow() {
      return canAccess("Cargos", "reading");
    },
    canEdit() {
      return canAccess("Cargos", "edition");
    },
    canDelete() {
      return canAccess("Cargos", "elimination");
    },
    canManageCharges() {
      return this.canShow || this.canEdit || this.canDelete;
    },
    tutorialSteps() {
      return buildCustomerChargesTour({
        t: this.$t.bind(this),
        canCreate: this.canCreate,
        canManage: this.canManageCharges,
        hasItems: this.items.length > 0,
        hasPagination: this.totalRows > this.perPage,
        isMobile: this.isMobileViewport,
      });
    },
    tutorialOptions() {
      return {
        highlight: true,
        enableScrolling: true,
        useKeyboardNavigation: true,
        startTimeout: 150,
        stopOnTargetNotFound: true,
      };
    },
    tutorialCallbacks() {
      return {
        onFinish: this.onTutorialFinish,
      };
    },
  },

  watch: {
    "$store.state.appConfig.activeCompanyGuid"(guid) {
      this.companyGuid = guid;
      this.hasNextPage = true;
      this.currentPage = 1;
      this.items = [];
      this.loadData();
    },
  },
  created() {
    this.companyGuid = this.$store.state.appConfig.activeCompanyGuid;
    this.loadData();
  },
  async mounted() {
    this.updateViewportMode();
    window.addEventListener("resize", this.updateViewportMode);
    const listContainer = document.getElementById("list-container");
    if (listContainer) {
      listContainer.addEventListener("scroll", this.handleScroll);
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateViewportMode);
    const listContainer = document.getElementById("list-container");
    if (listContainer) {
      listContainer.removeEventListener("scroll", this.handleScroll);
    }
  },
  methods: {
    updateViewportMode() {
      this.isMobileViewport = window.innerWidth < 768;
    },
    async startTutorial() {
      if (this.loading) return;

      this.updateViewportMode();
      await this.$nextTick();

      const tour = this.$tours[this.tutorialName];
      if (!tour || !this.tutorialSteps.length) return;

      const allTargetsExist = this.tutorialSteps.every((step) =>
        document.querySelector(step.target),
      );

      if (!allTargetsExist) {
        this.alertToast(
          "warning",
          this.$t("tutorials.common.unavailableTitle"),
          this.$t("tutorials.common.unavailableContent"),
          "AlertTriangleIcon",
        );
        return;
      }

      try {
        await tour.start();
      } catch (error) {
        this.onTutorialTargetNotFound();
      }
    },
    onTutorialFinish() {
      const idUser =
        this.$store.state.appConfig.sessionContext?.idUser || "user";
      localStorage.setItem(
        `sofia:tutorial:${idUser}:customerCharges:list:v1`,
        "completed",
      );
    },
    onTutorialTargetNotFound() {
      this.alertToast(
        "warning",
        this.$t("tutorials.common.unavailableTitle"),
        this.$t("tutorials.common.unavailableContent"),
        "AlertTriangleIcon",
      );
    },
    async loadData() {
      try {
        this.loading = true;
        const params = this.chargeParams();

        const [chargesResp] = await Promise.all([allCharges(params)]);

        // Procesamos empleados
        const charges = _.get(chargesResp, "data");
        this.items = charges?.items || [];
        this.totalRows = charges?.totalRows || 0;
        this.hasNextPage = charges?.currentPage < charges?.totalPages || 0;
      } catch (error) {
        this.alertToast("danger", "Error", error.message, "AlertTriangleIcon");
        console.error("Error loading data", error.message);
      } finally {
        this.loading = false;
      }
    },
    async loadChargesInfinity() {
      if (!this.hasNextPage) return;
      try {
        this.loading = true;
        const params = this.chargeParams();
        allCharges(params)
          .then((resp) => {
            const data = _.get(resp, "data", []);
            if (Array.isArray(data?.items)) {
              // Agregar nuevos elementos sin sobreescribir los existentes
              this.items = [...this.items, ...data?.items];
            }
            this.totalRows = data?.totalRows || 0;
            // Verificar si hay más páginas
            this.hasNextPage = data?.currentPage < data?.totalPages || false;
            this.loading = false;
          })
          .catch((error) => {
            this.alertToast(
              "danger",
              "Error",
              error.message,
              "AlertTriangleIcon",
            );
            console.error("Error loading products infinitely", error);
          });
      } catch (error) {
        console.error("Error loading data", error.message);
      }
    },
    label(field) {
      return `pages.charges.table.${field}`;
    },
    labelHeader(field) {
      return `pages.charges.table.tableHeader.${field}`;
    },
    chargeParams() {
      const arrParams = [];
      arrParams.push(`page,${this.currentPage}`);
      arrParams.push(`rows,${this.perPage}`);
      arrParams.push(`guidCompany,${this.companyGuid}`);

      if (this.appliedFilters.name) {
        arrParams.push(`name,${this.appliedFilters.name}`);
      }
      return getParams(arrParams);
    },
    async handleScroll() {
      const container = document.getElementById("list-container");
      if (
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 1
      ) {
        this.currentPage++;
        await this.loadChargesInfinity();
      }
    },
    handlePageChange(value) {
      this.currentPage = value;
      this.hasNextPage = true;
      this.loadData();
    },
    handlePerPageChange() {
      this.loadData();
    },
    validateFilters(filters) {
      // Validar nombre (no debe ser vacío ni contener solo espacios)
      if (filters.name && !filters.name.trim()) {
        console.error("El filtro de nombre no es válido.");
        return false;
      }
      return true;
    },
    applyFilters(filters) {
      if (this.validateFilters(filters)) {
        this.appliedFilters = filters;
        this.currentPage = 1;
        this.hasNextPage = true;
        this.loadData();
      } else {
        console.error("Filtros no aplicados debido a validaciones fallidas.");
      }
    },
    resetFilters() {
      this.appliedFilters = {};

      this.currentPage = 1;
      this.hasNextPage = true;
      this.loadData();
    },
    onCreate() {
      if (!this.canCreate) return;
      this.$router.push({
        name: "chargeCreate",
        params: { option: 1 },
      });
    },
    onShow(idRol) {
      if (!this.canShow) return;
      this.$router.push({
        name: "chargeEdit",
        params: { option: 2, idRol },
      });
    },
    onEdit(idRol) {
      if (!this.canEdit) return;
      this.$router.push({
        name: "chargeEdit",
        params: { option: 3, idRol },
      });
    },
    onDelete(idRol) {
      if (!this.canDelete) return;
      this.$swal({
        title: this.$t("areYouSure"),
        text: this.$t("willNotBeAble"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("yesDelete"),
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          showLoading();
          deleteCharge(idRol)
            .then((resp) => {
              stopLoading();
              this.$swal({
                icon: "success",
                title: this.$t("Deleted"),
                text: this.$t("beenDeleted"),
                customClass: {
                  confirmButton: "btn btn-success",
                },
              });
              this.loadData();
            })
            .catch((error) => {
              console.log("error", error.message);
              stopLoading();
              this.alertToast(
                "danger",
                this.$t("codeAlert"),
                error.message,
                "AlertTriangleIcon",
              );
            });
        } else if (result.dismiss === "cancel") {
          this.message(
            this.$t("canceled"),
            this.$t("registerDeletedCanceled"),
            "error",
          );
        }
      });
    },
    message(title, text, icon, html) {
      this.$swal({
        title,
        text,
        html,
        icon,
        customClass: {
          confirmButton: "btn btn-success",
        },
      });
    },
    alertToast(type = null, title = null, message = null, icon) {
      this.$bvToast.toast(message, {
        title,
        variant: type,
        solid: true,
        icon,
      });
    },
  },
};
</script>

<style lang="scss">
.per-page-selector {
  width: 90px;
}
.list-container {
  height: 60vh !important; /* Ajusta el alto según tus necesidades */
  overflow-y: auto;
}
</style>
